import React, { useState, useRef, useEffect } from 'react';
import { FiSearch, FiBell, FiLogOut, FiUser, FiSettings, FiClock, FiMenu } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import ProfileModal from './ProfileModal';
import './TopBar.css';

const TopBar = ({ user, workspace, onLogout, sidebarCollapsed, isMobile, onOpenMobileSidebar }) => {
  const location = useLocation();
  const [pageTitle, setPageTitle] = useState('Dashboard');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [marking, setMarking] = useState(false);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const titles = {
      '/dashboard': 'Dashboard',
      '/boards': 'Boards',
      '/my-work': 'My Work',
      '/global-tasks': 'Global Tasks',
      '/rfis': 'RFIs',
      '/submittals': 'Submittals',
      '/team-members': 'Team Members',
      '/activity-log': 'Activity Log',
      '/portfolio-gantt': 'Portfolio Gantt',
      '/finance-projects': 'Finance Projects',
    };
    // Match dynamic /boards/:id too
    const base = '/' + (location.pathname.split('/')[1] || '');
    setPageTitle(titles[location.pathname] || titles[base] || 'AA Project Management');
  }, [location.pathname]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(Number(data.unreadCount || 0));
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  // Refresh notifications when the dropdown opens
  useEffect(() => {
    if (showNotifications && user) {
      fetchNotifications();
    }
  }, [showNotifications]); // eslint-disable-line

  // Poll every 60s for new notifications while logged in
  useEffect(() => {
    if (!user) return;
    const intervalId = setInterval(fetchNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [user]); // eslint-disable-line

  const markAllAsRead = async () => {
    if (marking || unreadCount === 0) return;
    setMarking(true);
    // Optimistic update
    const prevNotifs = notifications;
    const prevUnread = unreadCount;
    setNotifications((items) => items.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) {
        // Revert on failure
        setNotifications(prevNotifs);
        setUnreadCount(prevUnread);
      }
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
      setNotifications(prevNotifs);
      setUnreadCount(prevUnread);
    } finally {
      setMarking(false);
    }
  };

  const markOneAsRead = async (notif) => {
    if (!notif || notif.read || !notif.id) return;
    // Optimistic update
    setNotifications((items) => items.map((n) => n.id === notif.id ? { ...n, read: true } : n));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notif.id}/mark-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const formatNotificationTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getNotificationTitle = (notif) => {
    const action = notif.actionType?.replace('_', ' ') || 'updated';
    const entity = notif.entityType?.replace('_', ' ') || 'item';
    return `${action} ${entity}: ${notif.entityName || 'Unknown'}`;
  };

  const companyName = workspace?.name || 'AA Architecture';
  const userName = user?.name || 'User';

  return (
    <div className={`top-bar ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isMobile ? 'mobile' : ''}`} data-testid="top-bar">
      <div className="top-bar-left">
        {isMobile && (
          <button
            className="top-bar-hamburger"
            onClick={onOpenMobileSidebar}
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <FiMenu size={22} />
          </button>
        )}
        <h1 className="top-bar-title" data-testid="page-title">{pageTitle}</h1>
        {!isMobile && (
          <span className="top-bar-welcome" data-testid="welcome-message">
            {companyName} • Welcome, {userName}
          </span>
        )}
      </div>
      <div className="top-bar-right">
        {!isMobile && (
          <div className="top-bar-search-container">
            <FiSearch className="top-bar-search-icon" />
            <input
              type="text"
              className="top-bar-search"
              placeholder="Search..."
              data-testid="search-input"
            />
          </div>
        )}
        {isMobile && (
          <button
            className="top-bar-icon-btn"
            onClick={() => setShowMobileSearch(!showMobileSearch)}
            data-testid="mobile-search-button"
            aria-label="Search"
          >
            <FiSearch size={20} />
          </button>
        )}
        <div ref={notifRef} style={{ position: 'relative' }}>
          <button
            className="top-bar-icon-btn"
            onClick={() => setShowNotifications(!showNotifications)}
            data-testid="notifications-button"
            aria-label="Notifications"
          >
            <FiBell size={20} />
            {unreadCount > 0 && (
              <span className="notification-badge" data-testid="notifications-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>
          {showNotifications && (
            <div className="dropdown notification-dropdown" data-testid="notifications-dropdown">
              <div className="notification-header">
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="notification-mark-all"
                    onClick={markAllAsRead}
                    disabled={marking}
                    data-testid="notifications-mark-all-read"
                  >
                    Mark all as read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="notification-empty">
                  <p>No new notifications</p>
                </div>
              ) : (
                <>
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`notification-item ${notif.read ? 'is-read' : 'is-unread'}`}
                      onClick={() => markOneAsRead(notif)}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      {!notif.read && <span className="notification-dot" aria-hidden="true"></span>}
                      <div className="notification-body">
                        <div className="notification-title">{getNotificationTitle(notif)}</div>
                        {notif.details && (
                          <div className="notification-details">{notif.details}</div>
                        )}
                        <div className="notification-time">{formatNotificationTime(notif.createdAt)}</div>
                      </div>
                    </div>
                  ))}
                  {notifications.length >= 10 && (
                    <>
                      <div className="dropdown-divider"></div>
                      <Link
                        to="/activity-log"
                        className="dropdown-item"
                        onClick={() => setShowNotifications(false)}
                        style={{ justifyContent: 'center', color: 'var(--monday-blue, #0073EA)', fontWeight: 500 }}
                      >
                        <FiClock size={14} />
                        <span>View All Activity</span>
                      </Link>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="top-bar-user-container" ref={userMenuRef}>
          <div
            className="top-bar-user"
            onClick={() => setShowUserMenu(!showUserMenu)}
            data-testid="user-menu-button"
          >
            <div className="avatar">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            {!isMobile && <span className="top-bar-user-name">{user?.name}</span>}
          </div>
          {showUserMenu && (
            <div className="dropdown user-dropdown" data-testid="user-dropdown">
              <div className="user-dropdown-header">
                <div className="avatar" style={{ marginRight: '12px' }}>
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <div className="user-dropdown-name">{user?.name}</div>
                  <div className="user-dropdown-email">{user?.email}</div>
                  <div className="user-dropdown-role">{user?.role}</div>
                </div>
              </div>
              <div className="dropdown-divider"></div>
              <div
                className="dropdown-item"
                onClick={() => {
                  setShowUserMenu(false);
                  setShowProfileModal(true);
                }}
                data-testid="profile-menu-item"
              >
                <FiUser size={16} />
                <span>Profile</span>
              </div>
              <div
                className="dropdown-item"
                onClick={() => {
                  setShowUserMenu(false);
                  setShowProfileModal(true);
                }}
                data-testid="settings-menu-item"
              >
                <FiSettings size={16} />
                <span>Settings</span>
              </div>
              <div className="dropdown-divider"></div>
              <div
                className="dropdown-item"
                onClick={() => {
                  setShowUserMenu(false);
                  onLogout();
                }}
                data-testid="logout-menu-item"
              >
                <FiLogOut size={16} />
                <span>Logout</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {isMobile && showMobileSearch && (
        <div className="top-bar-mobile-search">
          <FiSearch className="top-bar-search-icon" />
          <input
            type="text"
            className="top-bar-search"
            placeholder="Search..."
            data-testid="mobile-search-input"
            autoFocus
          />
        </div>
      )}

      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
        workspace={workspace}
        token={localStorage.getItem('token')}
        onUpdate={() => window.location.reload()}
      />
    </div>
  );
};

export default TopBar;
